<template>
  <FlotoContentLoader :loading="loading">
    <div class="flex flex-col flex-1">
      <MTab v-if="allTabs.length > 0" v-model="currentTab" position="left">
        <MTabPane v-for="tab in allTabs" :key="tab.type" :tab="tab.title">
          <CustomFieldTab
            v-if="tab.customField"
            :asset="asset"
            :disabled="disabled"
            :module-name="moduleName"
            :custom-fields-for-all="customFieldsForAll"
            :selected-asset-types="selectedAssetTypes"
            :custom-field-for-asset-type="availableCustomFieldForAssetType"
            :asset-type-id="assetTypeId"
            :update-api="updateApi"
            @change="handleFieldValueChange"
          />
          <PropertyComponentListContainer
            v-if="currentTab !== 'custom_field' && !tab.customField"
            :key="currentTab"
            :can-edit="!disabled"
            :can-create="!disabled"
            :type="tab.type"
            :fetch-fn="fetchData"
            :update-fn="updateData"
            :resource-id="resourceId"
            :resource="resource"
            :module-name="moduleName"
          />
        </MTabPane>
      </MTab>
    </div>
  </FlotoContentLoader>
</template>

<script>
import CloneDeep from 'lodash/cloneDeep'
import { FormComputed } from '@state/modules/form'
import { AssetTypeComputed } from '@state/modules/asset-type'
import { CITypeComputed } from '@state/modules/ci-type'
import { findValuePathWithItems } from '@data/recursive'
import {
  customFieldsForAllAssetType,
  customFieldAttributesTypePrefix,
  customFieldAttributesModuleName,
} from '@modules/asset/helpers/asset-custom-fields'
import { CustomRulesComputed } from '@state/modules/custom-rules'
import {
  getPropertyGroupApi,
  searchAssetPropertiesApi,
  updatePropertyGroupApi,
} from '../../asset-api'
import PropertyComponentListContainer from '../propery-component-list/propery-component-list-container'
import CustomFieldTab from './custom-field-tab'
export default {
  name: 'PropertyContainer',
  components: { PropertyComponentListContainer, CustomFieldTab },
  props: {
    resourceId: { type: Number, required: true },
    resource: { type: Object, required: true },
    updateApi: { type: Function, required: true },
    assetConfigInfo: { type: Object, required: true },
    moduleName: { type: String, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      asset: CloneDeep(this.resource),
      loading: true,
      tabs: [],
      propertyGroupList: [],
      currentTab: undefined,
    }
  },
  computed: {
    // @TODO refector custom field tab for asset
    ...FormComputed,
    ...AssetTypeComputed,
    ...CITypeComputed,
    ...CustomRulesComputed,
    assetTypeId() {
      const typePrefix = customFieldAttributesTypePrefix(this.moduleName)
      return this.resource[`${typePrefix}TypeId`]
    },
    customFields() {
      const typePrefix = customFieldAttributesTypePrefix(this.moduleName)
      return (
        ((this.assetForm || {}).fields || []).filter(
          (f) =>
            f.attributes.model ===
              customFieldAttributesModuleName(this.moduleName) &&
            !f.attributes[`${typePrefix}PropertyType`] &&
            !f.attributes[`${typePrefix}ComponentType`]
        ) || []
      )
    },
    selectedAssetTypes() {
      const typePrefix = customFieldAttributesTypePrefix(this.moduleName)
      if (this.asset[`${typePrefix}TypeId`]) {
        const collection = this[`${typePrefix}Types`]
        const assetTypeId = this.asset[`${typePrefix}TypeId`]
        const path = findValuePathWithItems(collection, assetTypeId)
        const rootItem = path[0]
        return {
          list: path,
          ...rootItem,
        }
      }
      return {}
    },
    customFieldsForAll() {
      return customFieldsForAllAssetType(this.customFields, this.moduleName)
    },
    availableCustomFieldForAssetType() {
      const typePrefix = customFieldAttributesTypePrefix(this.moduleName)
      if (!this.asset[`${typePrefix}TypeId`]) {
        return {}
      }
      const fullPath = this.selectedAssetTypes.list
      const assetTypeFields = {}
      fullPath.forEach((p) => {
        const c = this.customFields.filter(
          (f) => f.attributes[`${typePrefix}TypeId`] === String(p.id)
        )
        assetTypeFields[p.id] = c
      })
      return assetTypeFields
    },
    totalCustomFields() {
      const assetTypeFields = this.availableCustomFieldForAssetType
      let f = []
      Object.keys(assetTypeFields).map((i) => {
        f = f.concat(assetTypeFields[i])
      })
      return [...f, ...this.customFieldsForAll]
    },
    horizontalTabs() {
      return this.assetConfigInfo.property
    },
    propertyTabs() {
      const properties = this.propertyGroupList.filter(
        (g) => this.horizontalTabs.indexOf(g.model) >= 0
      )
      return properties.map((p) => ({
        formData: { ...p },
        type: p.model,
        title: this.__tc(p.model),
        key: p.model,
      }))
    },
    allTabs() {
      const propertyTabs = this.propertyTabs
      if (
        this.totalCustomFields.length &&
        propertyTabs.filter((p) => p.customField).length === 0
      ) {
        propertyTabs.push({
          customField: true,
          type: 'custom_field',
          title: this.$tc('custom_fields'),
          key: 'custom_field',
        })
      }
      return propertyTabs
    },
  },
  watch: {
    allTabs: {
      deep: true,
      handler(newValue) {
        if (this.moduleName === this.$constants.CMDB) {
          this.$emit('property-tab-data-blank', newValue.length <= 0)
        }
      },
    },
  },
  created() {
    this.getSupportData()
  },
  methods: {
    handleFieldValueChange(c) {
      this.asset = {
        ...this.asset,
        ...c,
        fieldValueDetails: {
          ...this.asset.fieldValueDetails,
          ...c,
        },
      }
    },
    getSupportData() {
      const typePrefix = customFieldAttributesTypePrefix(this.moduleName)
      if (!this.resourceId || !this.resource[`${typePrefix}TypeId`]) {
        return
      }
      Promise.all([getPropertyGroupApi(this.moduleName, this.resourceId)]).then(
        ([propertyGroups]) => {
          this.propertyGroupList = propertyGroups.assetPropertyGroupList
          this.loading = false
          this.$nextTick(() => {
            this.currentTab =
              this.horizontalTabs.length > 0
                ? this.horizontalTabs[0]
                : 'custom_field'
          })
        }
      )
    },
    fetchData() {
      if (this.currentTab && this.currentTab !== 'custom_field') {
        return searchAssetPropertiesApi(
          this.moduleName,
          this.resourceId,
          this.currentTab
        )
      }
    },
    updateData(data) {
      const message = this[`${this.moduleName}AskForApproval`]
        ? this.$tc('your_request_is_submitted_for_approval')
        : undefined
      return updatePropertyGroupApi(this.moduleName, data, message).then(
        (data) => {
          if (
            this[`${this.moduleName}AskForApproval`] &&
            this.resource.checkerApprovalStatus &&
            this.resource.checkerApprovalStatus !== 'pre_approved'
          ) {
            this.$emit('refresh', ['approval'])
          }
          return data
        }
      )
    },
  },
}
</script>
